/**
 * auto_run — the AUTO-RUN toggle's rules (sweep 2026-10-05). Pure: no DOM, no state import; the input module hands in
 * a context and asks.
 *
 *   THE RULE   one press of the `autoRun` action holds FORWARD (a camera-relative stick of 1, merged beside the keys
 *              and the pad exactly like the tour drive — never a synthetic KeyboardEvent). Sprint still applies: the
 *              intent mode is read from the merged forward intent.
 *   THE END    pressing it again · ANY movement key (forward, back, strafe) · the pad's left stick pushed · swimming
 *              or diving · mounting a vehicle / flying / the sector / fly or edit mode · a cinematic · the SWG
 *              placement map · a headset session · sitting down · the loading gate. A cancel names its reason.
 */
export interface AutoRunCtx {
  hoverRide: boolean; skyFlight: boolean; spaceFlight: boolean; flyMode: boolean; editMode: boolean; editorCursorMode: boolean;
  xrPresenting: boolean; loadingGate: boolean; placementView: boolean; cinematic: boolean; seated: boolean;
  /** swim / dive (the physics' water mode), or the head under the surface. */
  afloat: boolean;
}

export type AutoRunReason =
  | '' | 'toggle' | 'move-key' | 'pad-stick' | 'water' | 'vehicle' | 'flight' | 'space' | 'edit' | 'cinematic' | 'placement-map' | 'xr' | 'seated' | 'loading';

/** Why auto-run cannot be on right now ('' = it may run). Order = the most specific cause first. */
export function autoRunBlocker(c: AutoRunCtx): AutoRunReason {
  if (c.xrPresenting) return 'xr';
  if (c.loadingGate) return 'loading';
  if (c.cinematic) return 'cinematic';
  if (c.placementView) return 'placement-map';
  if (c.hoverRide) return 'vehicle';
  if (c.spaceFlight) return 'space';
  if (c.skyFlight) return 'flight';
  if (c.flyMode || c.editMode || c.editorCursorMode) return 'edit';
  if (c.seated) return 'seated';
  if (c.afloat) return 'water';
  return '';
}

/** Does pressing `code` end auto-run? True for any key bound to a walk direction. */
export function keyCancelsAutoRun(code: string, binds: { moveForward: readonly string[]; moveBack: readonly string[]; moveLeft: readonly string[]; moveRight: readonly string[] }): boolean {
  return binds.moveForward.includes(code) || binds.moveBack.includes(code) || binds.moveLeft.includes(code) || binds.moveRight.includes(code);
}

/** A pad's left stick (already deadzoned) pushed this far ends it — a drift in the deadzone's rim does not. */
export const PAD_CANCEL_STICK = 0.35;
export function padStickCancelsAutoRun(f: number, s: number): boolean {
  return Number.isFinite(f) && Number.isFinite(s) && Math.hypot(f, s) >= PAD_CANCEL_STICK;
}

/** The toggle's next state for a press: off → on only when nothing blocks it; on → off always. */
export function autoRunToggle(on: boolean, blocker: AutoRunReason): { on: boolean; reason: AutoRunReason } {
  if (on) return { on: false, reason: 'toggle' };
  return blocker ? { on: false, reason: blocker } : { on: true, reason: '' };
}

/** A modified press (Ctrl+= is the browser's zoom) never toggles. */
export function togglePressAllowed(e: { ctrlKey: boolean; metaKey: boolean; altKey: boolean; repeat: boolean }): boolean {
  return !e.ctrlKey && !e.metaKey && !e.altKey && !e.repeat;
}

/** The forward stick auto-run contributes (before the clamp with the keys / pad). */
export function autoRunForward(on: boolean): number { return on ? 1 : 0; }

/** The toast for a cancel the player did not cause with a key ('' = say nothing). */
export function cancelNotice(reason: AutoRunReason): string {
  switch (reason) {
    case 'water': return 'Auto-run stopped — you are in the water';
    case 'vehicle': case 'flight': case 'space': return 'Auto-run stopped — you are aboard';
    case 'cinematic': case 'placement-map': case 'edit': case 'xr': case 'seated': case 'loading': return 'Auto-run stopped';
    default: return '';
  }
}
